import tfaStatuses from '@/constants/tfa-status';
import API_AUTH_LOGIN from '@/services/api/authLogin.js';
import API_LOGIN from '@/services/api/login.js';
import API_LOGOUT from '@/services/api/logout';
import API_PROFILE from '@/services/api/profile.js';
import API_TFA from '@/services/api/twoFactorAuthenticatication.js';
import { deleteAllIndexedDB, isIndexedDBAvailable } from '@/utils/indexedDB';
import axios from 'axios';
import staticDataAction from './static-data';
import { clearTTDataAndFile } from './trade-trust';
import csatKeys from '@/constants/configuration/csat-keys.json';
import API_DEX from '@/services/api/dex.js';
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import API_RENEW_TOKEN from '@/services/api/renewToken.js';
import { useTradex } from '@/hooks/use-tradex/index';

const { env } = getEnvAndDex();

function handleTfa(user) {
	const { tfaRequired, tfaUserSetup, tfaVerified } = user

	let tfaStatus = tfaStatuses.none

	if (user?.isAdmin) {
		return tfaStatus = tfaStatuses.none
	}

	if (tfaVerified) {
		return tfaStatus = tfaStatuses.none
	}

	if (tfaUserSetup) {
		return tfaStatus = tfaStatuses.otp
	}

	if (tfaRequired) {
		return tfaStatus = tfaStatuses.setUp
	}

	return tfaStatus = tfaStatuses.none
}

async function handleUser(response) {
	const { user } = response

	let profile = [];

	if ((!user?.tfaRequired) || (user?.tfaRequired && user?.tfaVerified)) {
		profile = (await API_PROFILE.getProfile(user.id))[1]
	}

	return {
		profile,
	}
}

function login(goTo, credentials, flow) {

	return async (dispatch, getState) => {
		const version = getState().version.data;
		const isTradex = useTradex();
		let error, response

		switch (flow) {
			case "setUpTfa":
				[error, response] = await API_TFA.verifyTfa({ ...credentials })
				break;
			case "otpVerify":
				[error, response] = await API_TFA.validateTfa({ ...credentials })
				break;
			case "authLogin":
				[error, response] = await API_AUTH_LOGIN.create({ ...credentials })
				break;
			default:
				[error, response] = await API_LOGIN.create({ ...credentials, email: credentials.email.toLowerCase() })
				break;
		}

		const [dexError, dexResponse] = await API_DEX.index();

		const { features, apiSpecUrl } = dexResponse;
		const featuresList = features || [];

		if(dexError) {
			throw new Error("Failed to load dex config");
		}

		if (error) {
			if (!isTradex && error.message.includes("Unable to retrieve user from DB")) {
				throw new Error("Login failed. Please check your credentials.<br/>For further assistance, click the 'Get Help' widget on the right to contact support.");
			}
			if (error.message === 'Invalid credentials.' || error.message === 'Failed to Fetch User Details' || error.message === 'Invalid Login Credentials' || (String(error.message.toLowerCase()).includes("status code") && String(error.message.toLowerCase()).includes("400"))) {
				throw new Error('credentialError')
			}
			if (String(error.message.toLowerCase()).includes("status code") && !String(error.message.toLowerCase()).includes("400")) {
				throw new Error(!isTradex ? "Login failed. Please check your credentials.<br/>For further assistance, click the 'Get Help' widget on the right to contact support."
					: "An error occured! Please contact administrator.");
			}
			throw new Error(error.message)
		}

		const { token, systemId, user } = response

		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

		const { profile } = await handleUser(response)
		const tfaValue = handleTfa(user)

		sessionStorage.setItem('token', token)
		sessionStorage.setItem('user', JSON.stringify(user))
		sessionStorage.setItem('systemId', systemId)
		sessionStorage.setItem('profile', JSON.stringify(profile))
		sessionStorage.setItem('tfa', tfaValue)
		sessionStorage.setItem('features', JSON.stringify(featuresList))
		sessionStorage.setItem('apiSpecUrl', apiSpecUrl || '');
		if (featuresList.includes('SUBMITTALS') && dexResponse.submittalsHost) {
			sessionStorage.setItem('submittalsHost', dexResponse.submittalsHost);
		}

		if (isIndexedDBAvailable()) {
			deleteAllIndexedDB()
		}

		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

		dispatch({
			type: 'auth/login',
			payload: response,
			profile: profile,
			tfa: tfaValue,
			features: featuresList,
			apiSpecUrl: apiSpecUrl,
		})

		if (version.status === 'UPDATE_AVAILABLE') {
			sessionStorage.setItem("banner", true)
		}

		goTo(`/`)
	}
}

function logout(goTo) {
	return async (dispatch) => {
		dispatch(staticDataAction.removeStaticDataAction())
		await API_LOGOUT.create()
		sessionStorage.clear();

		if (isIndexedDBAvailable()) {
			dispatch(clearTTDataAndFile());
			deleteAllIndexedDB()
		}

		delete axios.defaults.headers.common['Authorization']

		// Removing only required keys from local storage due to CSAT Integration
		localStorage.removeItem('userEmail');
		localStorage.removeItem('lockedTime');
		localStorage.removeItem('lockedAccount');
		localStorage.removeItem('isTableView');
		localStorage.removeItem('isListView');

		// Remove CSAT Integration

		const csatWidgetBaseUrl = csatKeys[env]?.CSAT_WIDGET_BASE_URL;
		const csatSpaceKey = csatKeys[env]?.CSAT_SPACE_KEY;
		if (document.querySelector(`script[src="${csatWidgetBaseUrl}${csatSpaceKey}?onload=onUserSnapLoad"]`)) {
			document.querySelector(`script[src="${csatWidgetBaseUrl}${csatSpaceKey}?onload=onUserSnapLoad"]`).remove();
		}
		
		dispatch({ type: 'auth/logout' })
		goTo(`/login`)
		window.location.reload();
	}
}

function renewToken() {
	return async (dispatch) => {
		const currentToken = sessionStorage.getItem("token");
			const [error, response] = await API_RENEW_TOKEN.create(null, {
				headers: {
					Authorization: `Bearer ${currentToken}`,
				},
			});
			if (error) throw new Error(error.message);
			const { token, user } = response;
			axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
			sessionStorage.setItem("token", token);
			sessionStorage.setItem("user", JSON.stringify(user));
			dispatch({
				type: "auth/renewToken",
				payload: { token, user },
			});
	};
}

export {
	login,
	logout,
	renewToken
};

